import React, { useMemo } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  DataPagination,
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeader,
  DataTableRow,
  Label,
  RadioGroup,
  RadioGroupItem,
  Skeleton,
  Text,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@musa.tech/design-system';
import { useAppSelector } from '@/store/hooks';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { maskGeneratorName } from '@/utils/demoMode';
import { SearchableSelectField } from '@/components/collects/sob-demanda/SearchableSelectField';
import type { OnDemandContract } from '@/integrations/agreements/client';
import {
  BLOCK_REASON_LABELS,
  describeAdvance,
  describeBlockReason,
  describeMonthlyUsage,
  formatAddress,
  generatorName,
} from '@/utils/onDemandRequestRules';

export const CONTRACTS_PAGE_SIZE = 20;

export interface ContractSelectionStepProps {
  contracts: OnDemandContract[];
  total: number;
  page: number;
  onPageChange: (page: number) => void;
  isLoading: boolean;
  isError: boolean;
  /** Estabelecimento filtrado; vazio = todos do escopo. */
  generatorId: string;
  onGeneratorChange: (generatorId: string) => void;
  /** Time interno enxerga todos os geradores: só lista depois de escolher um. */
  requiresGenerator: boolean;
  selectedContractId: number | null;
  onSelect: (contract: OnDemandContract) => void;
  /** Acima do título — o seletor Coleta | Caçamba. */
  headerSlot?: React.ReactNode;
}

/**
 * Primeira etapa: o gerador escolhe o contrato. Resíduo, acondicionamento e endereço vêm dele —
 * não são campos da solicitação. O transportador não aparece: quem coleta é decisão da operação.
 */
export const ContractSelectionStep: React.FC<ContractSelectionStepProps> = ({
  contracts,
  total,
  page,
  onPageChange,
  isLoading,
  isError,
  generatorId,
  onGeneratorChange,
  requiresGenerator,
  selectedContractId,
  onSelect,
  headerSlot,
}) => {
  const { isDemoMode } = useDemoMode();

  return (
    <div className="flex flex-col gap-4">
      {headerSlot}
      <div className="flex flex-col gap-1">
        <Text as="h2" variant="heading" size="small" className="text-neutral-900">
          Escolha o contrato
        </Text>
        <Text variant="body" size="small" className="text-neutral-500">
          A coleta sob demanda é pedida sobre um contrato sem frequência definida. Resíduo,
          acondicionamento e endereço vêm dele.
        </Text>
      </div>

      <EstablishmentFilter
        generatorId={generatorId}
        onGeneratorChange={onGeneratorChange}
        requiresGenerator={requiresGenerator}
      />

      <ContractsBody
        contracts={contracts}
        isLoading={isLoading}
        isError={isError}
        waitingGenerator={requiresGenerator && !generatorId}
        selectedContractId={selectedContractId}
        onSelect={onSelect}
        isDemoMode={isDemoMode}
      />

      {total > CONTRACTS_PAGE_SIZE && (
        <DataPagination page={page} pageSize={CONTRACTS_PAGE_SIZE} total={total} onChange={onPageChange} />
      )}
    </div>
  );
};

/**
 * Estabelecimento do pedido. Aparece para quem tem mais de um, e sempre para o time interno,
 * que enxerga todos e precisa escolher antes de listar.
 */
export const EstablishmentFilter: React.FC<{
  generatorId: string;
  onGeneratorChange: (generatorId: string) => void;
  requiresGenerator: boolean;
}> = ({ generatorId, onGeneratorChange, requiresGenerator }) => {
  const establishments = useAppSelector((s) => s.establishmentAccess.establishments);
  const { isDemoMode } = useDemoMode();

  const establishmentItems = useMemo(
    () => establishments.map((e) => ({ value: String(e.id), label: maskGeneratorName(e.name, isDemoMode) })),
    [establishments, isDemoMode],
  );

  if (!requiresGenerator && establishments.length <= 1) return null;

  return (
    <div className="flex max-w-md flex-col gap-1">
      <Label>
        <Text variant="label" size="small" className="text-neutral-700">
          Estabelecimento
        </Text>
      </Label>
      <SearchableSelectField
        items={establishmentItems}
        value={generatorId}
        onChange={onGeneratorChange}
        placeholder={requiresGenerator ? 'Selecione o estabelecimento' : 'Todos os estabelecimentos'}
        searchPlaceholder="Buscar estabelecimento..."
        emptyText="Nenhum estabelecimento encontrado"
      />
    </div>
  );
};

interface ContractsBodyProps {
  contracts: OnDemandContract[];
  isLoading: boolean;
  isError: boolean;
  waitingGenerator: boolean;
  selectedContractId: number | null;
  onSelect: (contract: OnDemandContract) => void;
  isDemoMode: boolean;
}

const ContractsBody: React.FC<ContractsBodyProps> = ({
  contracts,
  isLoading,
  isError,
  waitingGenerator,
  selectedContractId,
  onSelect,
  isDemoMode,
}) => {
  if (waitingGenerator) {
    return (
      <EmptyMessage
        title="Selecione um estabelecimento"
        description="Escolha o estabelecimento para ver os contratos em que dá para pedir coleta sob demanda."
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2" data-testid="contracts-loading">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Não foi possível carregar os contratos</AlertTitle>
        <AlertDescription>
          O serviço de coletas está indisponível no momento. Tente novamente em alguns minutos.
        </AlertDescription>
      </Alert>
    );
  }

  if (!contracts.length) {
    return (
      <EmptyMessage
        title="Nenhum contrato sob demanda ativo"
        description="Só contratos sem frequência definida aceitam pedido pontual. Para pedir uma coleta avulsa, fale com a Musa e contrate um contrato sob demanda."
      />
    );
  }

  return (
    <RadioGroup
      value={selectedContractId != null ? String(selectedContractId) : ''}
      onValueChange={(value) => {
        const contract = contracts.find((c) => String(c.contractId) === value);
        if (contract?.requestable) onSelect(contract);
      }}
      className="block overflow-x-auto rounded-lg border border-border bg-white"
    >
      <DataTable>
        <DataTableHeader>
          <DataTableRow>
            <DataTableHead className="w-10" />
            <DataTableHead>Estabelecimento</DataTableHead>
            <DataTableHead>Resíduo</DataTableHead>
            <DataTableHead>Acondicionamento</DataTableHead>
            <DataTableHead>Regra</DataTableHead>
            <DataTableHead>Situação</DataTableHead>
          </DataTableRow>
        </DataTableHeader>
        <DataTableBody>
          {contracts.map((contract) => {
            const selected = contract.contractId === selectedContractId;

            return (
              <DataTableRow
                key={contract.contractId}
                data-testid={`contract-row-${contract.contractId}`}
                aria-selected={selected}
                aria-disabled={!contract.requestable}
                onClick={() => contract.requestable && onSelect(contract)}
                className={cn(
                  contract.requestable ? 'cursor-pointer' : 'cursor-not-allowed opacity-60',
                  selected && 'bg-primary-50',
                )}
              >
                <DataTableCell>
                  <RadioGroupItem
                    value={String(contract.contractId)}
                    aria-label={`Contrato ${contract.contractId}`}
                    disabled={!contract.requestable}
                  />
                </DataTableCell>
                <DataTableCell>
                  <Text variant="body" size="small" className="font-medium text-neutral-900">
                    {maskGeneratorName(generatorName(contract.generator), isDemoMode)}
                  </Text>
                  <Text variant="body" size="small" className="text-neutral-500">
                    {formatAddress(contract.address)}
                  </Text>
                </DataTableCell>
                <DataTableCell>{contract.material.name ?? '—'}</DataTableCell>
                <DataTableCell>{contract.packaging.name ?? '—'}</DataTableCell>
                <DataTableCell>
                  <Text variant="body" size="small" className="text-neutral-700">
                    {describeAdvance(contract.rule)}
                  </Text>
                  <Text variant="body" size="small" className="text-neutral-500">
                    {describeMonthlyUsage(contract)}
                  </Text>
                </DataTableCell>
                <DataTableCell>
                  <ContractSituation contract={contract} />
                </DataTableCell>
              </DataTableRow>
            );
          })}
        </DataTableBody>
      </DataTable>
    </RadioGroup>
  );
};

const ContractSituation: React.FC<{ contract: OnDemandContract }> = ({ contract }) => {
  if (contract.requestable) return <Badge variant="success" className="whitespace-nowrap">Disponível</Badge>;

  const reason = describeBlockReason(contract);
  const label = contract.blockReason ? BLOCK_REASON_LABELS[contract.blockReason] : 'Indisponível';

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0}>
            <Badge variant="warning" className="whitespace-nowrap">{label}</Badge>
          </span>
        </TooltipTrigger>
        {reason && <TooltipContent className="max-w-xs">{reason}</TooltipContent>}
      </Tooltip>
    </TooltipProvider>
  );
};

export const EmptyMessage: React.FC<{ title: string; description: string }> = ({ title, description }) => (
  <div className="flex flex-col items-center gap-1 rounded-lg border border-dashed border-neutral-200 px-6 py-10 text-center">
    <Text variant="label" size="medium" className="text-neutral-900">
      {title}
    </Text>
    <Text variant="body" size="small" className="max-w-md text-neutral-500">
      {description}
    </Text>
  </div>
);
